import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
};

export default function BlockedIpAddresses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unblockingId, setUnblockingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/system-settings/blocked-ip-addresses');
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao carregar endereços bloqueados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetReincidences = async (id) => {
    setResettingId(id);
    setError('');
    try {
      await api.post(`/system-settings/blocked-ip-addresses/${id}/reset-reincidences`);
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao zerar reincidências.');
    } finally {
      setResettingId(null);
    }
  };

  const unblock = async (id) => {
    setUnblockingId(id);
    setError('');
    try {
      await api.post(`/system-settings/blocked-ip-addresses/${id}/unblock`);
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao desbloquear endereço.');
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Endereços bloqueados</h1>
        <p className="text-sm text-slate-500">Monitore bloqueios automáticos por excesso de envios em formulários externos.</p>
      </div>

      {error ? <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-slate-500">Carregando...</div> : null}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Reincidências</th>
              <th className="px-4 py-3">Bloqueado até</th>
              <th className="px-4 py-3">Atualizado em</th>
              <th className="px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <tr className={item.active ? 'bg-red-50/40' : 'bg-white'}>
                  <td className="px-4 py-3 font-mono text-slate-800">{item.ip}</td>
                  <td className="px-4 py-3">{item.permanentlyBlocked ? 'Permanente' : item.active ? 'Bloqueado' : 'Liberado'}</td>
                  <td className="px-4 py-3">{item.violationCount}</td>
                  <td className="px-4 py-3">{item.permanentlyBlocked ? 'Permanente' : formatDate(item.blockedUntil)}</td>
                  <td className="px-4 py-3">{formatDate(item.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => resetReincidences(item.id)} disabled={resettingId === item.id || item.violationCount === 0} className="rounded-lg bg-slate-600 px-3 py-1.5 text-white disabled:opacity-50">
                        {resettingId === item.id ? 'Zerando...' : 'Zerar reincidências'}
                      </button>
                      <button type="button" onClick={() => unblock(item.id)} disabled={unblockingId === item.id} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-white disabled:opacity-50">
                        {unblockingId === item.id ? 'Desbloqueando...' : 'Desbloquear'}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colSpan="6" className="px-4 py-3 text-xs text-slate-600">
                    <strong>Histórico:</strong>{' '}
                    {Array.isArray(item.history) && item.history.length
                      ? item.history.slice(-8).reverse().map((event) => `${formatDate(event.at)} - ${event.type}`).join(' | ')
                      : 'Sem histórico registrado'}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {!items.length && !loading ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">Nenhum endereço registrado.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
